<template>
  <Toast />
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button :label="message.rejectLabel" outlined @click="rejectCallback" class="w-32"></Button>
          <Button :label="message.acceptLabel" @click="acceptCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <ModalCategory v-if="modalShowCategory" @closeModal="closeModal" :category="selectedCategory" />
  <IndexView />
  <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-30" :class="{ hidden: !isBlur }"></div>
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Kategori" />
      <div class="bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg">
        <div id="table-header" class="mb-5 flex justify-between">
          <AddButton to_url="#" button_text="+ Tambah Kategori" button_class="text-base py-2 font-medium"
            @click="showModal" />
        </div>
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow-lg rounded-lg">
              <TableComponent>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>No</TableHeadCell>
                    <TableHeadCell>Nama Kategori</TableHeadCell>
                    <TableHeadCell>Divisi</TableHeadCell>
                    <TableHeadCell>Status</TableHeadCell>
                    <TableHeadCell>Ubah Status</TableHeadCell>
                    <TableHeadCell>Action</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow v-if="allItemCategories?.length == 0">
                    <TableCell colspan="5" customClass="text-center italic text-gray-400 dark:text-gray-500"
                      noTextColor>
                      Belum ada data
                    </TableCell>
                  </TableRow>
                  <TableRow v-else v-for="(category, index) in allItemCategories" :key="category.id" :index="index"
                    isBody>
                    <TableCell>{{ index + 1 }}</TableCell>
                    <TableCell>{{ category.name }}</TableCell>
                    <TableCell>
                      {{ category.printer_list != null ? category.printer_list.map(item => item.name).join(", ") : "" }}
                    </TableCell>
                    <TableCell>
                      <span :class="{
                        'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300': category.status === 'active',
                        'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300': category.status === 'inactive'
                      }" class="px-2 py-1 rounded-full text-xs font-medium">
                        {{ category.status === 'active' ? "Aktif" : "Tidak Aktif" }}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button label="Ubah Status" severity="warn" @click="postStatus(category)" />
                    </TableCell>
                    <TableCell noTextColor customClass="text-gray-500 dark:text-gray-400 flex">
                      <button :id="'edit-data-' + category.id" @click="editCategory(category)"
                        class="border border-gray-400 hover:border-green-700 hover:bg-green-400 flex justify-center items-center mx-1 p-2 rounded-md group">
                        <PencilSquareIcon class="h-4 w-4 group-hover:text-green-700" />
                      </button>
                      <button :id="'delete-data-' + category.id" @click="deleteCategory(category)"
                        class="border border-gray-400 hover:border-red-500 hover:bg-red-400 flex justify-center items-center mx-1 p-2 rounded-md group">
                        <TrashIcon class="h-4 w-4 group-hover:text-red-700" />
                      </button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </TableComponent>
              <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
import IndexView from "@/views/IndexView.vue";
import PageTitle from "@/components/PageTitle.vue";
import AddButton from "@/components/buttons/AddButton.vue";
import ModalCategory from "@/components/modals/ModalItemCategory.vue";
import ViewContainer from "@/components/ViewContainer.vue";
import {
  TableComponent,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableCell,
} from "@/components/table";
import { PencilSquareIcon, TrashIcon } from "@heroicons/vue/24/solid";
import { mapGetters, mapActions } from "vuex";
import PaginationButtons from "@/components/PaginationButtons.vue";
import { Button, ConfirmDialog, Toast } from "primevue";

export default {
  name: "CategoryItemView",
  components: {
    IndexView, PageTitle, AddButton, ModalCategory, ViewContainer, TableComponent, TableHead, TableRow, TableHeadCell, TableBody, TableCell, PencilSquareIcon, TrashIcon, PaginationButtons, ConfirmDialog, Button, Toast
  },
  data() {
    return {
      modalShowCategory: false,
      isBlur: false,
      selectedCategory: null,
      currentPage: 1,
      totalPages: 1,
    }
  },
  computed: {
    ...mapGetters('item_categories', ['allItemCategories']),
    ...mapGetters('kitchen_divisions', ['allKitchenDivisions']),
  },
  created() {
    this.fetchItemCategories();
    this.$store.dispatch('kitchen_divisions/fetchKitchenDivisions');
  },
  watch: {
    currentPage: {
      immediate: true,
      handler(newVal) {
        this.fetchItemCategories({ page: newVal });
      },
    }
  },
  methods: {
    ...mapActions('item_categories', ['deleteItemCategoryById','updateItemCategory']),
    async fetchItemCategories(params = {}) {
      if (!params.page) params.page = this.currentPage;

      try {
        const { pagination } = await this.$store.dispatch('item_categories/fetchItemCategories', params);
        if (pagination) {
          this.totalPages = pagination.totalPages;
        }
      } catch (error) {
        console.log(error);
      }
    },
    showModal() {
      this.modalShowCategory = true;
      this.isBlur = true;
      this.selectedCategory = null;
    },
    closeModal() {
      setTimeout(() => {
        this.modalShowCategory = false;
        this.isBlur = false;
        this.fetchItemCategories();
      }, 200);
    },
    editCategory(category) {
      this.selectedCategory = category;
      this.modalShowCategory = true;
      this.isBlur = true;
    },
    async deleteCategory(category) {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin menghapus kategori ini?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: async () => {
          try {
            const response = await this.deleteItemCategoryById(category.id);
            console.log('response', response);
            this.fetchItemCategories();
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil menghapus kategori.', life: 3000 });
          }
          catch (error) {
            console.error('Gagal menghapus kategori.', error);
            this.$toast.add({ severity: 'error', summary: 'Failed', detail: 'Gagal menghapus kategori.', life: 3000 });
          }
        },
        reject: () => {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal menghapus kategori.', life: 3000 });
        }
      });
    },
    postStatus(category) {
        this.$confirm.require({
          group: 'headless',
          header: 'Apakah anda yakin ingin mengubah status kategory item '+category.name+'?',
          message: 'Silahkan konfirmasi sebelum diproses.',
          acceptLabel: 'Ya',
          rejectLabel: 'Tidak',
          accept: async () => {
            try {
              category.status = category.status === 'active' ? 'inactive' : 'active';
              const response = await this.updateItemCategory(category);
              console.log('response', response);
              this.fetchItemCategories();
              this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil mengubah status kategori.', life: 3000 });
            }
            catch (error) {
              console.error('Gagal mengubah status kategori.', error);
              this.$toast.add({ severity: 'error', summary: 'Failed', detail: error.response.data.message, life: 3000 });
            }
          },
          reject: () => {
            this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal mengubah status kategori.', life: 3000 });
          }
        });
      }
    },
  }
</script>